import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus, X, Copy, Check, AlertTriangle, Loader2 } from 'lucide-react';
import {
  fetchApiKeys,
  createApiKey,
  revokeApiKey,
  ApiKeyPublic,
  ApiKeyCreated,
} from '../services/api';

interface Props {
  tenantId: string;
  tenantName: string;
}

function formatTimestamp(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ status }: { status: 'active' | 'revoked' }) {
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
      status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
    }`}>
      {status}
    </span>
  );
}

function CreateKeyModal({
  tenantId,
  tenantName,
  onClose,
  onCreated,
}: {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  onCreated: (key: ApiKeyCreated) => void;
}) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createApiKey(tenantId, name.trim());
      onCreated(created);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create key');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">New API Key for {tenantName}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={submit}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Key name</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Masabi pa-pipeline"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            A label visible to you in this admin UI. The customer never sees it.
          </p>
          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Create key
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShowOnceModal({ created, onClose }: { created: ApiKeyCreated; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center gap-2 mb-3 text-amber-700">
          <AlertTriangle size={20} />
          <h3 className="text-lg font-semibold">Copy this key now</h3>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          This is the only time the full token will be shown. Store it somewhere safe
          (e.g. send it to the customer through a secure channel; they should put it
          straight into their secret manager).
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 font-mono text-sm break-all">
          {created.token}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={copy}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 flex items-center gap-2"
          >
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            I've saved it
          </button>
        </div>
      </div>
    </div>
  );
}

export const ApiKeysSection: React.FC<Props> = ({ tenantId, tenantName }) => {
  const [keys, setKeys] = useState<ApiKeyPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [justCreated, setJustCreated] = useState<ApiKeyCreated | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchApiKeys(tenantId);
      setKeys(data);
      setError(null);
    } catch {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (created: ApiKeyCreated) => {
    setShowCreate(false);
    setJustCreated(created);
    load();
  };

  const handleRevoke = async (key: ApiKeyPublic) => {
    if (!confirm(`Revoke key "${key.name}" (…${key.key_last4})? This cannot be undone.`)) return;
    setRevokingId(key.id);
    try {
      await revokeApiKey(tenantId, key.id);
      await load();
    } catch {
      alert('Revoke failed');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Key size={18} className="text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Public API Keys</h3>
          <span className="text-xs text-gray-500">({keys.length})</span>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus size={14} /> New key
        </button>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && keys.length === 0 && (
        <p className="text-sm text-gray-500">
          No API keys yet. Create one to let this tenant pull data from <code className="font-mono">/api/v1/public/*</code>.
        </p>
      )}

      {!loading && keys.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
            <tr>
              <th className="py-2 pr-3 font-medium">Name</th>
              <th className="py-2 pr-3 font-medium">Key</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 font-medium">Created</th>
              <th className="py-2 pr-3 font-medium">Last used</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-gray-100">
                <td className="py-2 pr-3 text-gray-900">{k.name}</td>
                <td className="py-2 pr-3 font-mono text-xs text-gray-600">{k.key_prefix}…{k.key_last4}</td>
                <td className="py-2 pr-3"><StatusBadge status={k.status} /></td>
                <td className="py-2 pr-3 text-gray-600">{formatTimestamp(k.created_at)}</td>
                <td className="py-2 pr-3 text-gray-600">{formatTimestamp(k.last_used_at)}</td>
                <td className="py-2 text-right">
                  {k.status === 'active' && (
                    <button
                      onClick={() => handleRevoke(k)}
                      disabled={revokingId === k.id}
                      className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      {revokingId === k.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreateKeyModal
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {justCreated && (
        <ShowOnceModal
          created={justCreated}
          onClose={() => setJustCreated(null)}
        />
      )}
    </section>
  );
};

export default ApiKeysSection;
